import { getLocationStateByCode, getLocationStateBySlug } from '../config/locationStates';
import { buildLocationSeoProfile } from './locationSeoProfile';
import { buildCanonicalPricingPath, normalizePathname, parseAddressParts, slugifySegment } from './pricingPath';

export const TRUCK_PARKING_BASE_PATH = '/truck-parking';
export const LEGACY_DISCOVERY_BASE_PATH = '/locations';
export const FIXED_TRUCK_PARKING_INTENT_SLUGS = Object.freeze(['overnight', 'secure', 'monthly']);

const TRUCK_PARKING_ROUTE_PATTERN = /^\/truck-parking(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?$/i;
const LEGACY_DISCOVERY_ROUTE_PATTERN = /^\/locations(?:\/([^/]+))?$/i;
const SECURE_FLAG_KEYS = Object.freeze(['gateAccess', 'fenced', 'securityCameras', 'lights']);
const DISCOVERY_CITY_ALIASES = Object.freeze({
  'ne-cartersville': { cityName: 'Cartersville', citySlug: 'cartersville' },
});
const toDisplayTitle = (value = '') => (
  String(value || '')
    .split('-')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
);

const normalizeLocationState = (location = null) => {
  if (!location || typeof location !== 'object') return null;

  const explicitState = String(location.state || '').trim();
  if (explicitState) {
    return getLocationStateByCode(explicitState) || getLocationStateBySlug(explicitState) || null;
  }

  const parsedAddress = parseAddressParts(location.address || '');
  return getLocationStateByCode(parsedAddress.stateCode) || null;
};

export const normalizeDiscoveryCity = (cityName = '') => {
  const normalizedName = String(cityName || '').trim();
  const citySlug = slugifySegment(normalizedName);
  if (!normalizedName || !citySlug) {
    return { cityName: '', citySlug: '' };
  }

  return DISCOVERY_CITY_ALIASES[citySlug] || { cityName: normalizedName, citySlug };
};

export const isSecureLocation = (location = null) => {
  const flags = location?.flags && typeof location.flags === 'object' ? location.flags : {};
  return SECURE_FLAG_KEYS.some((key) => flags[key] === true);
};

export const isDiscoveryEligibleLocation = (location = null) => (
  location?.availability === true
  && location?.openForParking === true
);

export const isMonthlyLocation = (location = null) => {
  const monthlyMode = String(location?.monthlySubscriptionMode || '').trim().toLowerCase();
  return (
    location?.openForMonthly === true
    || monthlyMode === 'off_app'
    || monthlyMode === 'in_app'
  );
};

export const isOvernightLocation = (location = null) => (
  location?.availability === true
  && location?.openForParking === true
  && location?.openForDaily === true
);

export const getCorridorIntentEntries = (location = null, profile = null) => {
  const resolvedProfile = profile || buildLocationSeoProfile(location);
  const highways = Array.isArray(resolvedProfile?.highways) ? resolvedProfile.highways : [];

  const seen = new Set();
  return highways
    .map((highway) => String(highway?.name || '').trim())
    .filter(Boolean)
    .map((name) => {
      const routeSlug = slugifySegment(name);
      if (!routeSlug) return null;
      const intentSlug = `near-${routeSlug}`;
      if (seen.has(intentSlug)) return null;
      seen.add(intentSlug);
      return {
        intentSlug,
        label: `Truck Parking Near ${name}`,
        routeName: name,
      };
    })
    .filter(Boolean);
};

export const buildFixedIntentEntries = (location = null) => {
  const intents = [];
  if (isOvernightLocation(location)) {
    intents.push({ intentSlug: 'overnight', label: 'Overnight Truck Parking' });
  }
  if (isSecureLocation(location)) {
    intents.push({ intentSlug: 'secure', label: 'Secure Truck Parking' });
  }
  if (isMonthlyLocation(location)) {
    intents.push({ intentSlug: 'monthly', label: 'Monthly Truck Parking' });
  }
  return intents;
};

export const buildTruckParkingStatePath = (stateSlug = '') => {
  const state = getLocationStateBySlug(stateSlug);
  if (!state) return TRUCK_PARKING_BASE_PATH;
  return `${TRUCK_PARKING_BASE_PATH}/${state.slug}`;
};

export const buildTruckParkingCityPath = ({ stateSlug = '', citySlug = '' } = {}) => {
  const normalizedCitySlug = slugifySegment(citySlug);
  if (!normalizedCitySlug) return buildTruckParkingStatePath(stateSlug);
  return `${buildTruckParkingStatePath(stateSlug)}/${normalizedCitySlug}`;
};

export const buildTruckParkingIntentPath = ({ stateSlug = '', citySlug = '', intentSlug = '' } = {}) => {
  const normalizedIntentSlug = String(intentSlug || '').trim().toLowerCase();
  if (!normalizedIntentSlug) return buildTruckParkingCityPath({ stateSlug, citySlug });
  return `${buildTruckParkingCityPath({ stateSlug, citySlug })}/${normalizedIntentSlug}`;
};

export const parseTruckParkingPath = (pathname = '/') => {
  const normalizedPath = normalizePathname(pathname);
  const truckParkingMatch = normalizedPath.match(TRUCK_PARKING_ROUTE_PATTERN);

  if (truckParkingMatch) {
    const [, rawStateSlug = '', rawCitySlug = '', rawIntentSlug = ''] = truckParkingMatch;
    const stateSlug = String(rawStateSlug || '').trim().toLowerCase();
    const citySlug = slugifySegment(rawCitySlug);
    const intentSlug = String(rawIntentSlug || '').trim().toLowerCase();

    if (!stateSlug) {
      return {
        isTruckParkingRoute: true,
        routeType: 'hub',
        pathname: normalizedPath,
        stateSlug: '',
        citySlug: '',
        intentSlug: '',
      };
    }

    if (!citySlug) {
      return {
        isTruckParkingRoute: true,
        routeType: 'state',
        pathname: normalizedPath,
        stateSlug,
        citySlug: '',
        intentSlug: '',
      };
    }

    if (!intentSlug) {
      return {
        isTruckParkingRoute: true,
        routeType: 'city',
        pathname: normalizedPath,
        stateSlug,
        citySlug,
        intentSlug: '',
      };
    }

    return {
      isTruckParkingRoute: true,
      routeType: 'intent',
      pathname: normalizedPath,
      stateSlug,
      citySlug,
      intentSlug,
    };
  }

  return {
    isTruckParkingRoute: false,
    routeType: null,
    pathname: normalizedPath,
    stateSlug: '',
    citySlug: '',
    intentSlug: '',
  };
};

export const parseLegacyDiscoveryPath = (pathname = '/') => {
  const normalizedPath = normalizePathname(pathname);
  const legacyMatch = normalizedPath.match(LEGACY_DISCOVERY_ROUTE_PATTERN);
  if (!legacyMatch) {
    return {
      isLegacyDiscoveryRoute: false,
      routeType: null,
      pathname: normalizedPath,
      stateSlug: '',
    };
  }

  return {
    isLegacyDiscoveryRoute: true,
    routeType: legacyMatch[1] ? 'state' : 'hub',
    pathname: normalizedPath,
    stateSlug: String(legacyMatch[1] || '').trim().toLowerCase(),
  };
};

export const buildLegacyTruckParkingRedirectPath = ({ pathname = '/', search = '' } = {}) => {
  const legacyRoute = parseLegacyDiscoveryPath(pathname);
  if (!legacyRoute.isLegacyDiscoveryRoute) return null;

  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const citySlug = slugifySegment(params.get('city') || '');
  params.delete('city');

  const nextStatePath = legacyRoute.stateSlug
    ? buildTruckParkingStatePath(legacyRoute.stateSlug)
    : TRUCK_PARKING_BASE_PATH;
  const nextPath = citySlug ? `${nextStatePath}/${citySlug}` : nextStatePath;
  const queryString = params.toString();
  return queryString ? `${nextPath}?${queryString}` : nextPath;
};

export const buildTruckParkingDiscoveryDataset = (locations = []) => {
  const stateMap = new Map();
  const stateOrder = [];

  (Array.isArray(locations) ? locations : []).forEach((location) => {
    if (!isDiscoveryEligibleLocation(location)) return;

    const state = normalizeLocationState(location);
    if (!state?.slug) return;

    const addressParts = parseAddressParts(location.address || '');
    const city = normalizeDiscoveryCity(addressParts.city || location.city || '');
    const cityName = city.cityName;
    const citySlug = city.citySlug;
    if (!cityName || !citySlug) return;

    const profile = buildLocationSeoProfile(location);
    const pricingPath = buildCanonicalPricingPath(location);
    if (!pricingPath) return;

    const fixedIntents = buildFixedIntentEntries(location);
    const corridorIntents = getCorridorIntentEntries(location, profile);
    const allIntentEntries = [...fixedIntents, ...corridorIntents];

    const locationRecord = {
      ...location,
      stateCode: state.code,
      stateName: state.name,
      stateSlug: state.slug,
      cityName,
      citySlug,
      pricingPath,
      profile,
      fixedIntents,
      corridorIntents,
      allIntentEntries,
    };

    let stateRecord = stateMap.get(state.slug);
    if (!stateRecord) {
      stateRecord = {
        stateCode: state.code,
        stateName: state.name,
        stateSlug: state.slug,
        locations: [],
        cityMap: new Map(),
      };
      stateMap.set(state.slug, stateRecord);
      stateOrder.push(state.slug);
    }

    stateRecord.locations.push(locationRecord);

    let cityRecord = stateRecord.cityMap.get(citySlug);
    if (!cityRecord) {
      cityRecord = {
        stateCode: state.code,
        stateName: state.name,
        stateSlug: state.slug,
        cityName,
        citySlug,
        locations: [],
        fixedIntentCounts: new Map(),
        corridorIntentCounts: new Map(),
      };
      stateRecord.cityMap.set(citySlug, cityRecord);
    }

    cityRecord.locations.push(locationRecord);
    fixedIntents.forEach((intent) => {
      const existing = cityRecord.fixedIntentCounts.get(intent.intentSlug) || {
        intentSlug: intent.intentSlug,
        label: intent.label,
        count: 0,
      };
      existing.count += 1;
      cityRecord.fixedIntentCounts.set(intent.intentSlug, existing);
    });
    corridorIntents.forEach((intent) => {
      const existing = cityRecord.corridorIntentCounts.get(intent.intentSlug) || {
        intentSlug: intent.intentSlug,
        label: intent.label,
        routeName: intent.routeName,
        count: 0,
      };
      existing.count += 1;
      cityRecord.corridorIntentCounts.set(intent.intentSlug, existing);
    });
  });

  const states = stateOrder
    .map((stateSlug) => stateMap.get(stateSlug))
    .filter(Boolean)
    .map((stateRecord) => {
      const cities = [...stateRecord.cityMap.values()]
        .map((cityRecord) => {
          const fixedIntents = FIXED_TRUCK_PARKING_INTENT_SLUGS
            .map((slug) => cityRecord.fixedIntentCounts.get(slug))
            .filter((entry) => entry && entry.count > 0);

          const corridorIntents = [...cityRecord.corridorIntentCounts.values()]
            .filter((entry) => entry.count > 0)
            .sort((left, right) => {
              if (right.count !== left.count) return right.count - left.count;
              return left.label.localeCompare(right.label);
            })
            .slice(0, 3);

          return {
            ...cityRecord,
            locations: [...cityRecord.locations].sort((left, right) => (
              String(left.title || left.address || left.id || '').localeCompare(
                String(right.title || right.address || right.id || '')
              )
            )),
            fixedIntents,
            corridorIntents,
            availableIntentSlugs: [
              ...fixedIntents.map((entry) => entry.intentSlug),
              ...corridorIntents.map((entry) => entry.intentSlug),
            ],
          };
        })
        .sort((left, right) => left.cityName.localeCompare(right.cityName));

      return {
        stateCode: stateRecord.stateCode,
        stateName: stateRecord.stateName,
        stateSlug: stateRecord.stateSlug,
        locations: [...stateRecord.locations].sort((left, right) => (
          String(left.cityName || '').localeCompare(String(right.cityName || ''))
        )),
        cities,
      };
    });

  return {
    states,
    stateMap: new Map(states.map((state) => [state.stateSlug, state])),
  };
};

export const getStateDiscoveryRecord = (dataset = null, stateSlug = '') => {
  if (!dataset?.stateMap || !stateSlug) return null;
  const state = getLocationStateBySlug(stateSlug);
  if (!state?.slug) return null;
  return dataset.stateMap.get(state.slug) || null;
};

export const getCityDiscoveryRecord = (dataset = null, stateSlug = '', citySlug = '') => {
  const stateRecord = getStateDiscoveryRecord(dataset, stateSlug);
  if (!stateRecord || !citySlug) return null;
  const normalizedCitySlug = slugifySegment(citySlug);
  return stateRecord.cities.find((city) => city.citySlug === normalizedCitySlug) || null;
};

export const getIntentDiscoveryRecord = (dataset = null, stateSlug = '', citySlug = '', intentSlug = '') => {
  const cityRecord = getCityDiscoveryRecord(dataset, stateSlug, citySlug);
  if (!cityRecord) return null;

  const normalizedIntentSlug = String(intentSlug || '').trim().toLowerCase();
  if (!normalizedIntentSlug) return null;

  const fixedIntent = cityRecord.fixedIntents.find((entry) => entry.intentSlug === normalizedIntentSlug);
  if (fixedIntent) {
    return {
      ...fixedIntent,
      kind: 'fixed',
      locations: cityRecord.locations.filter((location) => (
        location.fixedIntents.some((entry) => entry.intentSlug === normalizedIntentSlug)
      )),
      city: cityRecord,
    };
  }

  const corridorIntent = cityRecord.corridorIntents.find((entry) => entry.intentSlug === normalizedIntentSlug);
  if (corridorIntent) {
    return {
      ...corridorIntent,
      kind: 'corridor',
      locations: cityRecord.locations.filter((location) => (
        location.corridorIntents.some((entry) => entry.intentSlug === normalizedIntentSlug)
      )),
      city: cityRecord,
    };
  }

  return null;
};

export const getIntentDisplayLabel = (intentSlug = '') => {
  if (intentSlug === 'overnight') return 'Overnight Truck Parking';
  if (intentSlug === 'secure') return 'Secure Truck Parking';
  if (intentSlug === 'monthly') return 'Monthly Truck Parking';
  if (String(intentSlug || '').startsWith('near-')) {
    return `Truck Parking Near ${toDisplayTitle(String(intentSlug || '').replace(/^near-/, ''))}`;
  }
  return 'Truck Parking';
};

export const getLocationTruckParkingRouteMeta = (location = null) => {
  if (!location || typeof location !== 'object') return null;
  if (!isDiscoveryEligibleLocation(location)) return null;

  const state = normalizeLocationState(location);
  if (!state?.slug) return null;

  const addressParts = parseAddressParts(location.address || '');
  const city = normalizeDiscoveryCity(addressParts.city || location.city || '');
  const cityName = city.cityName;
  const citySlug = city.citySlug;
  if (!cityName || !citySlug) return null;

  const profile = buildLocationSeoProfile(location);
  const corridorIntentSlugs = Array.isArray(profile.highways)
    ? profile.highways
      .map((highway) => String(highway?.name || '').trim())
      .filter(Boolean)
      .map((name) => `near-${slugifySegment(name)}`)
      .filter((slug, index, items) => slug !== 'near-' && items.indexOf(slug) === index)
    : [];

  return {
    stateCode: state.code,
    stateName: state.name,
    stateSlug: state.slug,
    cityName,
    citySlug,
    pricingPath: buildCanonicalPricingPath(location),
    intentSlugs: [
      ...(isOvernightLocation(location) ? ['overnight'] : []),
      ...(isSecureLocation(location) ? ['secure'] : []),
      ...(isMonthlyLocation(location) ? ['monthly'] : []),
      ...corridorIntentSlugs,
    ],
  };
};

export const filterLocationsForTruckParkingRoute = (locations = [], routeMeta = null) => {
  if (!routeMeta?.isTruckParkingRoute) return Array.isArray(locations) ? locations : [];

  // Hub route (/truck-parking) is the full directory — include all locations (daily AND
  // monthly-only). Sub-routes (state/city/intent) still gate on discovery eligibility
  // so their geographic filters have valid data to work with.
  if (routeMeta.routeType === 'hub') {
    return Array.isArray(locations) ? locations : [];
  }

  return (Array.isArray(locations) ? locations : []).filter((location) => {
    const locationRouteMeta = getLocationTruckParkingRouteMeta(location);
    if (!locationRouteMeta) return false;

    if (locationRouteMeta.stateSlug !== routeMeta.stateSlug) return false;
    if (routeMeta.routeType === 'state') return true;
    if (locationRouteMeta.citySlug !== routeMeta.citySlug) return false;
    if (routeMeta.routeType === 'city') return true;
    return locationRouteMeta.intentSlugs.includes(routeMeta.intentSlug);
  });
};

export default {
  FIXED_TRUCK_PARKING_INTENT_SLUGS,
  LEGACY_DISCOVERY_BASE_PATH,
  TRUCK_PARKING_BASE_PATH,
  buildFixedIntentEntries,
  buildLegacyTruckParkingRedirectPath,
  buildTruckParkingCityPath,
  buildTruckParkingDiscoveryDataset,
  buildTruckParkingIntentPath,
  buildTruckParkingStatePath,
  filterLocationsForTruckParkingRoute,
  getCityDiscoveryRecord,
  getCorridorIntentEntries,
  getIntentDiscoveryRecord,
  getIntentDisplayLabel,
  getLocationTruckParkingRouteMeta,
  getStateDiscoveryRecord,
  isDiscoveryEligibleLocation,
  isMonthlyLocation,
  isOvernightLocation,
  isSecureLocation,
  normalizeDiscoveryCity,
  parseLegacyDiscoveryPath,
  parseTruckParkingPath,
};
